/**
 * Legal Service
 *
 * Handles legal document retrieval and user acceptance tracking.
 *
 * Database Tables:
 * - legal_documents: Stores versioned legal documents (Terms, Privacy)
 * - user_legal_acceptances: Tracks which users accepted which document versions
 *
 * @module LegalService
 */

import { supabase } from '@/lib/supabaseGuard';
import { processGoogleImageToCloudinary } from '@/utils/imageProcessing';

/**
 * V272.7: Ensure user row exists in users table
 * This prevents FK violations when inserting into user_legal_acceptances
 *
 * IMPORTANT: This function now calls the ensure-user-row edge function
 * instead of doing direct database upserts (which violate RLS)
 *
 * @param userId - User ID from auth.user.id
 * @returns true if user row exists or was created, false otherwise
 */
async function ensureUserRowExists(userId: string): Promise<boolean> {
  console.log('🔍 V272.7: Checking if user row exists:', userId);

  try {
    // Check if user row already exists
    const { data: existingUser, error: checkError } = await supabase
      .from('users')
      .select('id')
      .eq('id', userId)
      .maybeSingle();

    if (checkError) {
      console.error('❌ V272.7: Error checking user existence:', checkError);
      return false;
    }

    if (existingUser) {
      console.log('✅ V272.7: User row already exists');
      return true;
    }

    // V272.7: User row doesn't exist - call edge function to create it
    console.log('🔧 V272.7: User row missing - calling ensure-user-row edge function');

    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) {
      console.error('❌ V272.7: Cannot get auth user');
      return false;
    }

    // V207.21: Get Google name FIRST (needed for photo upload)
    const googleName = authUser.user_metadata?.name || authUser.user_metadata?.full_name || null;
    console.log('📋 V275.9: Google name from auth metadata:', googleName);

    // V275.9: CRITICAL - Check if user already has Cloudinary photo BEFORE uploading
    // This prevents overwriting real photos with Google avatars!
    console.log('[V275.9] LegalService: Checking for existing Cloudinary photo...');

    const { data: userPhotoCheck } = await supabase
      .from('users')
      .select('photo_urls')
      .eq('id', userId)
      .maybeSingle();

    const hasCloudinaryPhoto = userPhotoCheck?.photo_urls?.some((url: string) =>
      url && url.includes('cloudinary.com')
    );

    console.log('[V275.9] LegalService: User has Cloudinary photo:', hasCloudinaryPhoto);

    // V207.21: Process Google image through Cloudinary (5:4 WebP) with userName
    // V275.9: ONLY if user doesn't already have a Cloudinary photo!
    const avatarUrl = authUser.user_metadata?.avatar_url;
    let photoUrl = avatarUrl;

    if (avatarUrl?.includes('googleusercontent.com') && !hasCloudinaryPhoto) {
      console.log('🖼️ V275.9: LegalService processing Google image (no Cloudinary photo exists)');
      try {
        const cloudinaryUrl = await processGoogleImageToCloudinary(
          avatarUrl,
          userId,
          googleName || undefined  // V207.21: Pass Google name for Cloudinary display name
        );
        photoUrl = cloudinaryUrl || (avatarUrl.split('=s')[0] + '=s2048');
        console.log('✅ V275.9: LegalService photo processed:', photoUrl);
      } catch (error) {
        console.error('❌ V275.9: LegalService processing failed:', error);
        photoUrl = avatarUrl.split('=s')[0] + '=s2048';
      }
    } else if (hasCloudinaryPhoto) {
      console.log('[V275.9] ✅ SKIPPING Google avatar upload - user already has Cloudinary photo');
      console.log('[V275.9] Existing photo:', userPhotoCheck.photo_urls[0]?.substring(0, 80));
      // Don't set photoUrl - let edge function preserve existing photo
      photoUrl = null;
    }

    // V275.9: Call edge function instead of direct upsert (bypasses RLS)
    // V275.9: Only include photo_urls if we actually have a new photo to set
    const userData: Record<string, any> = {
      name: googleName,
      google_name: googleName
    };

    // V275.9: Only add photo_urls if we have a new photo (not preserving existing)
    if (photoUrl) {
      userData.photo_urls = [photoUrl];
      console.log('[V275.9] LegalService: Including new photo_urls in edge function call');
    } else {
      console.log('[V275.9] LegalService: NOT including photo_urls (preserving existing)');
    }

    const { data, error } = await supabase.functions.invoke('ensure-user-row', {
      body: {
        userId: userId,
        userEmail: authUser.email || '',
        userData: userData
      }
    });

    if (error) {
      console.error('❌ V272.7: Edge function failed:', error);
      return false;
    }

    if (!data?.user) {
      console.error('❌ V272.7: Edge function succeeded but returned no user');
      return false;
    }

    console.log('✅ V272.7: User row created via edge function:', data.user.id);
    return true;
  } catch (error) {
    console.error('❌ V272.7: Exception in ensureUserRowExists:', error);
    return false;
  }
}

export interface LegalDocument {
  id: string;
  doc_type: 'terms' | 'privacy' | 'guidelines' | 'refund' | 'wedding_gift_policy' | 'pricing' | 'safety' | 'subscription_management';
  language: string;
  version: string;
  title: string;
  body: string;
  short_summary: string | null;
  effective_date: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface LegalAcceptance {
  id: string;
  user_id: string;
  legal_document_id: string;
  accepted_at: string;
  client_ip?: string;
  user_agent?: string;
}

class ActiveLegalDocumentNotFoundError extends Error {
  constructor(docType: LegalDocument['doc_type'], language: string) {
    super(`No active ${docType} document found for language ${language}`);
    this.name = 'ActiveLegalDocumentNotFoundError';
  }
}

/**
 * Get the active legal document for a specific type and language
 *
 * @param docType - 'terms', 'privacy', 'guidelines', 'refund', or 'wedding_gift_policy'
 * @param language - Language code (default: 'en')
 * @returns Active legal document or null if not found
 */
export async function getActiveDocument(
  docType: 'terms' | 'privacy' | 'guidelines' | 'refund' | 'wedding_gift_policy' | 'pricing' | 'safety' | 'subscription_management',
  language: string = 'en'
): Promise<LegalDocument | null> {
  console.log('🚀 V179.2: Fetching legal document from DB only');
  console.log(`🔍 LegalService: getActiveDocument(${docType}, ${language})`);

  const { data, error } = await supabase
    .from('legal_documents')
    .select('*')
    .eq('doc_type', docType)
    .eq('language', language)
    .eq('is_active', true)
    .single();

  if (error || !data) {
    console.error('❌ V179.2: DB fetch failed, showing error state');
    console.error(`❌ LegalService: Error fetching ${docType}:`, error);
    console.error(`❌ LegalService: Error code:`, error?.code);
    console.error(`❌ LegalService: Error details:`, error?.details);
    console.error(`❌ LegalService: Error hint:`, error?.hint);
    console.error(`❌ LegalService: Query params - doc_type: ${docType}, language: ${language}, is_active: true`);
    if (!data && (!error || error.code === 'PGRST116')) {
      throw new ActiveLegalDocumentNotFoundError(docType, language);
    }

    // V179.2: Throw error instead of returning null - NO silent fallback
    throw new Error(`Failed to load ${docType} document: ${error?.message || 'Document not found'}`);
  }

  console.log('✅ V179.2: Loaded terms v' + data.version);
  console.log(`✅ LegalService: Found ${docType} v${data.version}`);
  console.log(`✅ LegalService: Document ID: ${data.id}, Body length: ${data.body?.length || 0}`);
  return data as LegalDocument;
}

/**
 * Resolve an active legal document in the selected language, then English.
 * If neither lookup succeeds, preserve the existing error behavior.
 */
export async function getActiveDocumentWithEnglishFallback(
  docType: LegalDocument['doc_type'],
  language: string = 'en'
): Promise<LegalDocument> {
  try {
    const document = await getActiveDocument(docType, language);
    if (document) return document;
  } catch (localizedError) {
    if (language === 'en' || !(localizedError instanceof ActiveLegalDocumentNotFoundError)) {
      throw localizedError;
    }

    console.warn(
      `⚠️ LegalService: No active ${docType} document for ${language}; falling back to English`
    );
  }

  const englishDocument = await getActiveDocument(docType, 'en');
  if (!englishDocument) {
    throw new Error(`No active ${docType} document found for ${language} or en`);
  }
  return englishDocument;
}

/**
 * Get all versions of a legal document (for admin use)
 * 
 * @param docType - 'terms' or 'privacy'
 * @param language - Language code (default: 'en')
 * @returns Array of legal documents
 */
export async function listDocumentVersions(
  docType: 'terms' | 'privacy',
  language: string = 'en'
): Promise<LegalDocument[]> {
  console.log(`🔍 LegalService: listDocumentVersions(${docType}, ${language})`);

  const { data, error } = await supabase
    .from('legal_documents')
    .select('*')
    .eq('doc_type', docType)
    .eq('language', language)
    .order('version', { ascending: false });

  if (error) {
    console.error(`❌ LegalService: Error listing ${docType} versions:`, error);
    return [];
  }

  console.log(`✅ LegalService: Found ${data.length} versions of ${docType}`);
  return data as LegalDocument[];
}

/**
 * Get active versions of all legal document types (for Settings display)
 * V179: Fetches versions from database instead of hardcoded values
 * 
 * @param language - Language code (default: 'en')
 * @returns Object mapping doc_type to version string
 */
export async function getActiveDocumentVersions(
  language: string = 'en'
): Promise<Record<string, string>> {
  console.log('🚀 V179: Fetching legal document versions from DB');

  const { data, error } = await supabase
    .from('legal_documents')
    .select('doc_type, version')
    .eq('language', language)
    .eq('is_active', true);

  if (error) {
    console.error('❌ LegalService: Error fetching document versions:', error);
    return { terms: '1.0', privacy: '1.0', guidelines: '1.0', refund: '1.0', wedding_gift_policy: '1.0', pricing: '1.0', safety: '1.0' }; // Fallback - all versions updated to 1.0
  }

  const versions: Record<string, string> = {};
  data.forEach((doc) => {
    versions[doc.doc_type] = doc.version;
  });

  console.log('✅ V179: Loaded legal document versions:', versions);
  return versions;
}

/**
 * Record a user's acceptance of a legal document
 * 
 * @param userId - User ID
 * @param documentId - Legal document ID
 * @param clientIp - Optional client IP address
 * @param userAgent - Optional user agent string
 * @returns Acceptance record or null if failed
 */
export async function recordAcceptance(
  userId: string,
  documentId: string,
  clientIp?: string,
  userAgent?: string
): Promise<LegalAcceptance | null> {
  console.log(`📝 LegalService: recordAcceptance(user=${userId}, doc=${documentId})`);

  const { data, error} = await supabase
    .from('user_legal_acceptances')
    .insert({
      user_id: userId,
      legal_document_id: documentId,
      client_ip: clientIp,
      user_agent: userAgent,
    })
    .select()
    .single();

  if (error) {
    console.error('❌ LegalService: Error recording acceptance:', error);
    return null;
  }

  console.log('✅ LegalService: Acceptance recorded successfully');
  return data as LegalAcceptance;
}

/**
 * Check if a user has accepted the latest version of a legal document
 * 
 * @param userId - User ID
 * @param docType - 'terms' or 'privacy'
 * @param language - Language code (default: 'en')
 * @returns true if user has accepted latest version, false otherwise
 */
export async function hasUserAcceptedLatest(
  userId: string,
  docType: 'terms' | 'privacy',
  language: string = 'en'
): Promise<boolean> {
  console.log(`🔍 LegalService: hasUserAcceptedLatest(user=${userId}, type=${docType})`);

  // Get the latest active document
  const latestDoc = await getActiveDocument(docType, language);
  if (!latestDoc) {
    console.error(`❌ LegalService: No active ${docType} document found`);
    return false;
  }

  // Check if user has accepted this document
  const { data, error } = await supabase
    .from('user_legal_acceptances')
    .select('id')
    .eq('user_id', userId)
    .eq('legal_document_id', latestDoc.id)
    .single();

  if (error) {
    // No acceptance found (404 is expected if user hasn't accepted)
    if (error.code === 'PGRST116') {
      console.log(`ℹ️  LegalService: User has not accepted ${docType} v${latestDoc.version}`);
      return false;
    }
    console.error('❌ LegalService: Error checking acceptance:', error);
    return false;
  }

  console.log(`✅ LegalService: User has accepted ${docType} v${latestDoc.version}`);
  return true;
}

/**
 * Get all acceptances for a user (for admin/compliance use)
 * 
 * @param userId - User ID
 * @returns Array of acceptance records with document details
 */
export async function getUserAcceptances(userId: string): Promise<any[]> {
  console.log(`🔍 LegalService: getUserAcceptances(user=${userId})`);

  const { data, error } = await supabase
    .from('user_legal_acceptances')
    .select(`
      *,
      legal_documents:legal_document_id (
        doc_type,
        version,
        title,
        effective_date
      )
    `)
    .eq('user_id', userId)
    .order('accepted_at', { ascending: false });

  if (error) {
    console.error('❌ LegalService: Error fetching user acceptances:', error);
    return [];
  }

  console.log(`✅ LegalService: Found ${data.length} acceptances for user`);
  return data;
}

/**
 * Check if user needs to accept legal documents
 * Returns object with terms and privacy acceptance status
 * 
 * @param userId - User ID
 * @returns Object with terms and privacy acceptance status
 */
export async function checkAcceptanceStatus(userId: string): Promise<{
  needsTermsAcceptance: boolean;
  needsPrivacyAcceptance: boolean;
  termsDocument: LegalDocument | null;
  privacyDocument: LegalDocument | null;
}> {
  console.log(`🔍 LegalService: checkAcceptanceStatus(user=${userId})`);

  const [hasAcceptedTerms, hasAcceptedPrivacy, termsDoc, privacyDoc] = await Promise.all([
    hasUserAcceptedLatest(userId, 'terms'),
    hasUserAcceptedLatest(userId, 'privacy'),
    getActiveDocument('terms'),
    getActiveDocument('privacy'),
  ]);

  const result = {
    needsTermsAcceptance: !hasAcceptedTerms,
    needsPrivacyAcceptance: !hasAcceptedPrivacy,
    termsDocument: termsDoc,
    privacyDocument: privacyDoc,
  };

  console.log('✅ LegalService: Acceptance status:', result);
  return result;
}

// ============================================================================
// V180: Server-Side Consent Tracking Functions
// ============================================================================

export interface V180LegalAcceptance {
  doc_type: 'terms' | 'privacy' | 'guidelines';
  doc_version: string;
  accepted_at: string;
}

export interface V180AcceptanceRecord {
  user_id: string;
  legal_document_id: string;  // ✅ V180.2: ONLY save document ID (not doc_type/doc_version)
  ip_address?: string;
  user_agent?: string;
}

/**
 * V180.2: Check if user has accepted all current legal docs (by document ID only)
 *
 * @param userId - User ID
 * @returns Object with acceptance status and missing documents
 */
export async function checkUserAcceptances(userId: string): Promise<{
  hasAcceptedAll: boolean;
  missing: string[];
  acceptances: V180LegalAcceptance[];
}> {
  console.log('🚀 V180.2: Checking user legal acceptances');

  // Get active document IDs from legal_documents
  const { data: activeDocs, error: docsError } = await supabase
    .from('legal_documents')
    .select('id, doc_type, version')
    .eq('is_active', true);

  if (docsError || !activeDocs) {
    throw new Error('Failed to fetch active legal documents');
  }

  // Get user's acceptances (only document IDs)
  const { data: acceptances, error: accError } = await supabase
    .from('user_legal_acceptances')
    .select('legal_document_id, accepted_at')
    .eq('user_id', userId);

  if (accError) {
    throw new Error('Failed to fetch user acceptances');
  }

  // Check which docs are missing acceptance (by ID)
  const missing: string[] = [];
  const acceptedIds = new Set(acceptances?.map(a => a.legal_document_id) || []);

  for (const doc of activeDocs) {
    if (!acceptedIds.has(doc.id)) {
      missing.push(doc.doc_type);
    }
  }

  console.log('✅ V180.2: Acceptance check complete', {
    total: activeDocs.length,
    accepted: activeDocs.length - missing.length,
    missing
  });

  return {
    hasAcceptedAll: missing.length === 0,
    missing,
    acceptances: acceptances || []
  };
}

// LegalDocumentSimple is used for getActiveLegalDocuments (subset of fields)
type LegalDocumentSimple = {
  id: string;
  doc_type: 'terms' | 'privacy' | 'guidelines';
  version: string;
  title: string;
};

/**
 * V180.5: Fetch all active legal documents we care about (terms / privacy / guidelines)
 * for the current language (adjust language filter if needed).
 */
export async function getActiveLegalDocuments(language: 'en' | 'he' = 'en'): Promise<LegalDocument[]> {
  const { data, error } = await supabase
    .from('legal_documents')
    .select('id, doc_type, version, title')
    .eq('is_active', true)
    .eq('language', language)
    .in('doc_type', ['terms', 'privacy', 'guidelines']);

  if (error) {
    console.error('V180.5 – failed to load active legal documents', error);
    throw error;
  }

  return (data ?? []) as LegalDocument[];
}

/**
 * V180.2: Insert/update user consent for ALL legal documents in a SINGLE row
 *
 * SCHEMA: user_legal_acceptances has ONE row per user with:
 * - terms_id (FK to legal_documents)
 * - privacy_id (FK to legal_documents)
 * - guidelines_id (FK to legal_documents)
 *
 * This is NOT one row per document type - it's ONE row with all three IDs.
 */
export async function recordLegalAcceptances(params: {
  userId: string;
  documents: LegalDocument[];
  clientIp?: string | null;
  userAgent?: string | null;
}) {
  console.log('🚀 V180.2: recordLegalAcceptances CALLED');
  const { userId, documents, clientIp, userAgent } = params;
  console.log('🚀 V180.2: userId:', userId);
  console.log('🚀 V180.2: documents:', documents);

  if (!userId) {
    throw new Error('recordLegalAcceptances: missing userId');
  }
  if (!documents?.length) {
    console.log('⚠️ V180.2: No documents to record');
    return;
  }

  // V205.3: Ensure user row exists before inserting legal acceptances
  console.log('🔍 V205.3: Ensuring user row exists before recording legal acceptances...');
  const userExists = await ensureUserRowExists(userId);
  if (!userExists) {
    console.error('❌ V205.3: Cannot record legal acceptances - user row creation failed');
    throw new Error('Failed to create user record - cannot record legal acceptances');
  }
  console.log('✅ V205.3: User row confirmed, proceeding with legal acceptances');

  // Extract the three document IDs by type
  const termsDoc = documents.find(d => d.doc_type === 'terms');
  const privacyDoc = documents.find(d => d.doc_type === 'privacy');
  const guidelinesDoc = documents.find(d => d.doc_type === 'guidelines');

  if (!termsDoc || !privacyDoc || !guidelinesDoc) {
    console.error('❌ V180.2: Missing required documents');
    console.error('❌ V180.2: Found:', {
      terms: !!termsDoc,
      privacy: !!privacyDoc,
      guidelines: !!guidelinesDoc
    });
    throw new Error('Missing required legal documents (need all 3: terms, privacy, guidelines)');
  }

  console.log('📄 V180.2: Found all required docs:', {
    terms: termsDoc.version,
    privacy: privacyDoc.version,
    guidelines: guidelinesDoc.version
  });

  // Create SINGLE row with all three IDs
  const record = {
    user_id: userId,
    terms_id: termsDoc.id,
    privacy_id: privacyDoc.id,
    guidelines_id: guidelinesDoc.id,
    ip_address: clientIp ?? null,
  };

  console.log('📝 V180.2: Inserting single row:', record);

  const { data, error } = await supabase
    .from('user_legal_acceptances')
    .upsert(record, {
      onConflict: 'user_id,terms_id,privacy_id,guidelines_id'
    })
    .select('*');

  console.log('🔍 V180.2: Upsert result - data:', data);
  console.log('🔍 V180.2: Upsert result - error:', error);

  if (error) {
    console.error('❌ V180.2: Failed to record legal acceptances:', error);
    console.error('❌ V180.2: Error details:', JSON.stringify(error, null, 2));
    throw error;
  }

  console.log('✅ V180.2: Successfully recorded acceptance for all 3 documents');
  return data;
}

/**
 * V180.2: Check whether the user already accepted ALL active documents.
 *
 * SCHEMA: user_legal_acceptances has ONE row per user with terms_id, privacy_id, guidelines_id
 * So we just check if the user has ANY row (which means they accepted all 3)
 */
export async function hasAcceptedAllLegal(params: {
  userId: string;
  language?: 'en' | 'he';
}): Promise<{
  hasAll: boolean;
  activeDocs: LegalDocument[];
  missingDocIds: string[];
}> {
  const { userId, language = 'en' } = params;

  console.log('🔍 V180.2: Checking if user has accepted all legal docs');

  if (!userId) {
    return { hasAll: false, activeDocs: [], missingDocIds: [] };
  }

  const activeDocs = await getActiveLegalDocuments(language);
  if (!activeDocs.length) {
    console.log('⚠️ V180.2: No active documents found');
    return { hasAll: true, activeDocs: [], missingDocIds: [] }; // nothing to enforce
  }

  console.log('📄 V180.2: Found active docs:', activeDocs.map(d => `${d.doc_type} v${d.version}`));

  // Check if user has ANY acceptance record
  // (Since schema requires all 3 IDs, having a row means they accepted all)
  const { data, error } = await supabase
    .from('user_legal_acceptances')
    .select('id, terms_id, privacy_id, guidelines_id, accepted_at')
    .eq('user_id', userId);

  if (error) {
    console.error('❌ V180.2: Failed to check user legal acceptances:', error);
    throw error;
  }

  const hasAccepted = data && data.length > 0;

  console.log('✅ V180.2: Check complete:', {
    hasAccepted,
    recordCount: data?.length || 0
  });

  // If user has a record, they've accepted all (no missing docs)
  // If no record, all docs are missing
  const missingDocIds = hasAccepted ? [] : activeDocs.map(d => d.id);

  return {
    hasAll: hasAccepted,
    activeDocs,
    missingDocIds,
  };
}

/**
 * V180: Helper to get client IP (best effort)
 *
 * @returns IP address or null if unavailable
 */
async function getClientIP(): Promise<string | null> {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch {
    return null;
  }
}

/**
 * V180.3.1: Ensure user has legal consent record
 * Auto-creates if missing (for existing users backfill)
 *
 * This function is called on every login to automatically backfill
 * consent records for existing users without requiring explicit acceptance.
 *
 * @param userId - User ID to check/create consent for
 */
export async function ensureUserConsent(userId: string): Promise<void> {
  console.log('🔍 V180.3.1: ensureUserConsent CALLED');
  console.log('🔍 V180.3.1: userId:', userId);

  if (!userId) {
    console.error('❌ V180.3.1: No userId provided');
    return;
  }

  try {
    // Check if user already has a record
    console.log('🔍 V180.3.1: Checking if user has accepted all legal docs...');
    const result = await hasAcceptedAllLegal({ userId });
    console.log('🔍 V180.3.1: hasAcceptedAllLegal result:', result);

    if (result.hasAll) {
      console.log('✅ V180.3.1: User already has consent record');
      return;
    }

    console.log('⚠️ V180.3.1: No consent record found, creating...');

    // Get active documents
    console.log('🔍 V180.3.1: Fetching active legal documents...');
    const activeDocs = await getActiveLegalDocuments('en');
    console.log('🔍 V180.3.1: Active docs:', activeDocs?.length || 0);

    if (!activeDocs || activeDocs.length === 0) {
      console.error('❌ V180.3.1: No active legal documents found');
      return;
    }

    if (activeDocs.length !== 3) {
      console.error('❌ V180.3.1: Expected 3 active docs, found:', activeDocs.length);
      return;
    }

    // Get client IP (best effort)
    console.log('🔍 V180.3.1: Fetching client IP...');
    const clientIp = await getClientIP();
    console.log('🔍 V180.3.1: Client IP:', clientIp || 'not available');

    // Auto-create consent record for existing user
    console.log('🔍 V180.3.1: Calling recordLegalAcceptances...');
    await recordLegalAcceptances({
      userId,
      documents: activeDocs,
      clientIp,
      userAgent: navigator?.userAgent || null,
    });

    console.log('✅ V180.3.1: Auto-created consent record successfully');
  } catch (error) {
    console.error('❌ V180.3.1: Failed to create consent:', error);
    console.error('❌ V180.3.1: Error stack:', error instanceof Error ? error.stack : 'No stack trace');
    // Don't throw - allow user to continue even if consent creation fails
  }
}
